import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { utils } from "xlsx";
import Dialog from "../../../../../../components/shared/Dialog";
import Button from "../../../../../../components/shared/Button";
import BaseForm, { Field } from "../../../../../../components/shared/Form";
import UploadInput from "../../../../../../components/shared/inputs/UploadInput";
import ColumnSelect from "./ColumnSelect";
import { useGroups } from "../../../../../Settings/General/OrganisationLevelGroups/context";
import { useLoadWorkSheet } from "./useLoadWorkSheet";
import useApi from "../../../../../../utils/api";
import { renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";
import { getErrorMessage } from "../../../../../../utils/helpers/errors";
import { withLoadingToast } from "../../../helpers/withLoadingToast";
import { createImportSchema, type ImportFormValues } from "./zodValidation";
import type { ImportModalProps } from "./types";

function ImportModal({ isOpen, close }: ImportModalProps) {
    const { t } = useTranslation();
    const [file, setFile] = useState<File | null>(null);
    const { allocatedGroups } = useGroups();
    const { workSheet } = useLoadWorkSheet(file);
    const { authPost } = useApi();

    const schema = useMemo(() => createImportSchema({ t }), [t]);

    const form = useForm<ImportFormValues>({
        resolver: zodResolver(schema)
    });

    const formValues = useWatch({ control: form.control });

    const columns = useMemo(() => {
        if (!workSheet) {
            return [];
        }

        return utils
            .sheet_to_csv(workSheet)
            .split("\n")[0]
            .split(",")
            .filter((item) => !Object.values(formValues ?? {}).includes(item));
    }, [workSheet, formValues]);

    const mutation = useMutation({
        ...withLoadingToast(t("importing")),
        mutationFn: (values: ImportFormValues) => {
            const data = new FormData();
            data.append("file", file as File);
            data.append("mapping", JSON.stringify(values));

            return authPost("/schedules/import", {
                data,
                headers: { "Content-Type": "multipart/form-data" }
            });
        },
        onSuccess: () => {
            toast.success(t("schedule-imported-successfully"));
            onClose();
        },
        onError: (err) => getErrorMessage(err, t)
    });

    const onClose = useCallback(() => {
        setFile(null);
        form.reset();
        close();
    }, [close, form]);

    const onFileChange = useCallback((nextFile: File | null) => {
        setFile(nextFile);
        form.reset();
    }, [form]);

    const onFinish = useCallback((values: ImportFormValues) => {
        mutation.mutate(values);
    }, [mutation]);

    return (
        <Dialog title={t("schedule-import")} open={isOpen} close={onClose} size="2xl">
            <div>
                <UploadInput
                    value={file}
                    onChange={onFileChange}
                    accept=".csv,.xlsx,.xls"
                />

                {file && (
                    <BaseForm form={form} onFinish={onFinish} className="tw-mt-4">
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4">
                            <Field name="employeeNumber" label={t("employee-number")} required>
                                <ColumnSelect columns={columns} />
                            </Field>

                            <Field name="job" label={t("job")}>
                                <ColumnSelect columns={columns} />
                            </Field>

                            <Field name="location" label={t("location")}>
                                <ColumnSelect columns={columns} />
                            </Field>
                        </div>

                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4">
                            <Field name="date" label={t("date")} required>
                                <ColumnSelect columns={columns} />
                            </Field>

                            {/* only units are not required to have this field */}
                            <Field name="startDate" label={t("start-date")} required>
                                <ColumnSelect columns={columns} />
                            </Field>

                            {/* only units are not required to have this field */}
                            <Field name="endDate" label={t("end-date")} required>
                                <ColumnSelect columns={columns} />
                            </Field>
                        </div>

                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
                            {allocatedGroups.map((group: any) => (
                                <Field
                                    key={group.id}
                                    name={group.level.replace("_", "")}
                                    label={renderSettingsResourceLabel(group)}
                                >
                                    <ColumnSelect columns={columns} />
                                </Field>
                            ))}
                        </div>

                        <Button className="tw-w-full" loading={mutation.isPending} disabled={mutation.isPending}>
                            {t("submit")}
                        </Button>
                    </BaseForm>
                )}
            </div>
        </Dialog>
    );
}

export default ImportModal;
